import React from 'react';
import { Link } from 'react-router-dom';
import Modal, { ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import { PetStatus, STATUS_LABELS, STATUS_COLORS, speciesEmoji, kgToLbs } from '../../lib/utils';
import { trackPetDeviceLinkClicked } from '../../analytics';
import styles from './ViewPetModal.module.scss';

interface ViewPetData {
  id: string;
  name: string;
  gender?: string | null;
  species?: string | null;
  weight?: number | null;
  color?: string | null;
  additionalInfo?: string | null;
  updatedAt?: string | null;
  device?: { id: string; moduleId: string; lastConnectionState?: { date: string } | null } | null;
  chip?: { id: string; shortId: string; serialNumber: string } | null;
  breed?: { name: string } | null;
  yearOfBirth?: number | null;
  monthOfBirth?: number | null;
  dayOfBirth?: number | null;
  status?: string;
}

interface ViewPetModalProps {
  open: boolean;
  onClose: () => void;
  pet: ViewPetData | null;
  onAssignDevice?: (petId: string) => void;
  onAddChip?: (petId: string) => void;
  onRemovePet?: (petId: string, petName: string) => void;
  onEditPet?: (petId: string) => void;
}

function titleCase(value?: string | null): string {
  if (!value) {
    return '\u2014';
  }
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function formatDob(pet: ViewPetData): string {
  if (!pet.yearOfBirth) {
    return '\u2014';
  }
  const parts: string[] = [];
  if (pet.monthOfBirth) {
    const monthName = new Date(2000, pet.monthOfBirth - 1).toLocaleDateString('en-US', { month: 'long' });
    parts.push(monthName);
  }
  if (pet.dayOfBirth) {
    parts.push(String(pet.dayOfBirth));
  }
  parts.push(String(pet.yearOfBirth));
  return parts.join(' ');
}

function formatDate(iso?: string | null): string {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function ViewPetModal({
  open,
  onClose,
  pet,
  onAssignDevice,
  onAddChip,
  onRemovePet,
  onEditPet,
}: ViewPetModalProps) {
  if (!pet) {
    return null;
  }

  const status = (pet.status ?? 'rescue') as PetStatus;
  const subtitle = [pet.breed?.name, titleCase(pet.gender)].filter((v) => v && v !== '\u2014').join(' \u00B7 ');

  return (
    <Modal open={open} onClose={onClose}>
      {/* Hero */}
      <div className={styles.hero}>
        <div className={styles.heroContent}>
          <div className={styles.avatar}>{speciesEmoji(pet.species)}</div>
          <div className={styles.heroText}>
            <div className={styles.petName}>{pet.name}</div>
            {subtitle && <div className={styles.petSubtitle}>{subtitle}</div>}
            <div className={styles.statusRow}>
              <span className={styles[`badge_${STATUS_COLORS[status]}`]}>{STATUS_LABELS[status] ?? 'Rescue'}</span>
              {pet.updatedAt && <span className={styles.addedDate}>Added {formatDate(pet.updatedAt)}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Details Grid */}
      <div className={styles.body}>
        <div className={styles.detailsGrid}>
          <div className={styles.gridCell}>
            <div className={styles.cellLabel}>Born</div>
            <div className={styles.cellValue}>{formatDob(pet)}</div>
          </div>
          <div className={styles.gridCell}>
            <div className={styles.cellLabel}>Weight</div>
            <div className={styles.cellValue}>{kgToLbs(pet.weight) ? `${kgToLbs(pet.weight)} lbs` : '\u2014'}</div>
          </div>
          <div className={styles.gridCell}>
            <div className={styles.cellLabel}>Color</div>
            <div className={styles.cellValue}>{pet.color ?? '\u2014'}</div>
          </div>
          <div className={styles.gridCell}>
            <div className={styles.cellLabel}>Spay/Neuter</div>
            <div className={styles.cellValue}>{pet.additionalInfo || 'Unknown'}</div>
          </div>
          <div className={styles.gridCell}>
            <div className={styles.cellLabel}>Species</div>
            <div className={styles.cellValue}>{titleCase(pet.species)}</div>
          </div>
          <div className={styles.gridCell}>
            <div className={styles.cellLabel}>Breed</div>
            <div className={styles.cellValue}>{pet.breed?.name ?? '\u2014'}</div>
          </div>
        </div>

        {/* Hardware */}
        <div className={styles.sectionLabel}>Hardware</div>
        <div className={styles.hardwareRow}>
          {pet.chip ? (
            <div className={styles.hwCardChip}>
              <span className={styles.hwIconChip}>&#9672;</span>
              <div>
                <div className={styles.hwTitle}>Microchip</div>
                <div className={styles.hwSerial}>{pet.chip.serialNumber}</div>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className={styles.hwCardEmpty}
              onClick={() => {
                if (onAddChip) {
                  onClose();
                  onAddChip(pet.id);
                }
              }}
            >
              <span className={styles.hwIconEmpty}>&mdash;</span>
              <div>
                <div className={styles.hwTitleMuted}>No Chip</div>
                <div className={styles.hwAddLink}>+ Add Chip</div>
              </div>
            </button>
          )}

          {pet.device ? (
            <Link
              to={`/rescue/devices?search=${encodeURIComponent(pet.device.moduleId)}`}
              onClick={() => {
                trackPetDeviceLinkClicked();
                onClose();
              }}
              className={styles.hwCardDevice}
            >
              <span className={styles.hwIconDevice}>&#9673;</span>
              <div>
                <div className={styles.hwTitle}>GPS Device</div>
                <div className={styles.hwDeviceId}>{pet.device.moduleId}</div>
              </div>
            </Link>
          ) : (
            <button
              type="button"
              className={styles.hwCardEmpty}
              onClick={() => {
                if (onAssignDevice) {
                  onClose();
                  onAssignDevice(pet.id);
                }
              }}
            >
              <span className={styles.hwIconEmpty}>&mdash;</span>
              <div>
                <div className={styles.hwTitleMuted}>No Device</div>
                <div className={styles.hwAddLink}>+ Add Device</div>
              </div>
            </button>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <div style={{ flex: 1 }} />
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
          {onEditPet && status !== 'adopted' && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                onEditPet(pet.id);
                onClose();
              }}
            >
              Edit Profile
            </Button>
          )}
          {onRemovePet && (
            <Button
              variant="danger"
              size="sm"
              disabled={!!pet.device || !!pet.chip}
              title={pet.device || pet.chip ? 'Remove hardware before deleting' : undefined}
              onClick={() => {
                onRemovePet(pet.id, pet.name);
                onClose();
              }}
            >
              Delete Pet
            </Button>
          )}
        </div>
        {(pet.device || pet.chip) && onRemovePet && (
          <div className={styles.footerHint}>Remove hardware to enable delete</div>
        )}
      </div>
    </Modal>
  );
}
